import { useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from "framer-motion";

import aboutImg from "../../assets/about.jpeg";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// ─── Styled ───────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');
  *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
  :root{
    --cream:#F7F3EE; --cream-dark:#EDE7DC; --warm-tan:#C9AA8C;
    --terracotta:#B87355; --mocha:#7A5C47; --ink:#2E1F14;
    --ink-soft:#5C4033; --gold:#C49A6C;
    --font-serif:'Cormorant Garamond',Georgia,serif;
    --font-sans:'Jost',sans-serif;
  }
  html{scroll-behavior:smooth;}
  body{
    background:var(--cream); color:var(--ink);
    font-family:var(--font-sans); font-weight:300;
    -webkit-font-smoothing:antialiased; overflow-x:hidden;
  }
`;

const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--cream);
`;

const Track = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// ── LEFT — sticky image ───────────────────────────────────────────────
const ImageCol = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  align-self: flex-start;

  @media (max-width: 768px) {
    position: relative;
    height: 50vh;
    width: 100%;
  }
`;

const Img = styled(motion.div)`
  position: absolute;
  inset: -10% 0;
  background-image: url(${aboutImg});
  background-size: contain;
  background-position: center 15%;
  will-change: transform;
  background-repeat: no-repeat;
`;

// Subtle right-edge fade so image bleeds into the text side
const ImgFade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 60%, var(--cream) 100%);
  z-index: 2;
`;

// ── RIGHT — scrolling content ─────────────────────────────────────────
const TextCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 12vh 7vw 18vh 5vw;
  gap: 0;

  @media (max-width: 768px) {
    padding: 5rem 6vw 8rem;
  }
`;

// ── Section label at top of text col ─────────────────────────────────
const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 5rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 32px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;

// ── Name block ────────────────────────────────────────────────────────
const NameBlock = styled.div`
  margin-bottom: 6rem;
`;
const NameWrap = styled.div`
  overflow: hidden;
`;
const Name = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(4rem, 7vw, 7.5rem);
  font-weight: 300;
  line-height: 0.88;
  color: var(--ink);
  letter-spacing: -0.02em;
`;
const NameItalic = styled(Name)`
  font-style: italic;
  color: var(--terracotta);
`;

// ── Text blocks that scroll in one by one ────────────────────────────
const TextBlock = styled.div`
  margin-bottom: 5rem;
  max-width: 44ch;
`;

const BlockLabel = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.58rem;
  font-weight: 300;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--warm-tan);
  display: block;
  margin-bottom: 1.2rem;
`;

const BlockText = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.88rem, 1.1vw, 1rem);
  font-weight: 200;
  letter-spacing: 0.04em;
  line-height: 1.95;
  color: var(--ink-soft);
`;

const BlockQuote = styled(motion.p)`
  font-family: var(--font-serif);
  font-size: clamp(1.3rem, 2vw, 1.8rem);
  font-weight: 300;
  font-style: italic;
  line-height: 1.45;
  color: var(--ink);
  letter-spacing: 0.01em;
`;

// ── Divider between blocks ─────────────────────────────────────────────
const Divider = styled(motion.div)`
  width: 36px;
  height: 1px;
  background: var(--warm-tan);
  opacity: 0.45;
  margin-bottom: 5rem;
`;

// ── Credentials ───────────────────────────────────────────────────────
const CredList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-bottom: 5rem;
`;
const CredRow = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid rgba(122, 92, 71, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;
const CredKey = styled.span`
  font-family: var(--font-sans);
  font-size: 0.62rem;
  font-weight: 300;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--warm-tan);
`;
const CredVal = styled.span`
  font-family: var(--font-sans);
  font-size: 0.72rem;
  font-weight: 300;
  letter-spacing: 0.06em;
  color: var(--ink);
`;

// ── CTA ───────────────────────────────────────────────────────────────
const CtaBlock = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 2rem;
`;
const CtaBtn = styled(motion.button)`
  font-family: var(--font-sans);
  font-size: 0.7rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--ink);
  border: none;
  padding: 1.1rem 2.8rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--terracotta);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  span {
    position: relative;
    z-index: 1;
  }
`;
const CtaLink = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 300;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--mocha);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(5px);
  }
`;

// ── Signature ─────────────────────────────────────────────────────────
const Signature = styled(motion.div)`
  margin-top: 5rem;
  font-family: var(--font-serif);
  font-size: clamp(2rem, 3.5vw, 3.2rem);
  font-weight: 300;
  font-style: italic;
  color: var(--warm-tan);
  opacity: 0.45;
  letter-spacing: -0.01em;
`;

// ─── Reusable fade-up block ───────────────────────────────────────────
function FadeBlock({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-12% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ─── Reveal line ──────────────────────────────────────────────────────
function RevealLine({ children, delay = 0, as: Tag = motion.h2, ...props }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <NameWrap ref={ref}>
      <Tag
        initial={{ y: "105%" }}
        animate={inView ? { y: "0%" } : {}}
        transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
        {...props}
      >
        {children}
      </Tag>
    </NameWrap>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────
export default function AboutSection() {
  const trackRef = useRef(null);
  const { t } = useTranslation();

  // Parallax on the image as user scrolls through the section
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  const rawY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const imgY = useSpring(rawY, { stiffness: 60, damping: 20 });
  const credentials = [
    {
      key: t("about.credentials.location_key"),
      val: t("about.credentials.location_val"),
    },
    {
      key: t("about.credentials.languages_key"),
      val: t("about.credentials.languages_val"),
    },
    {
      key: t("about.credentials.trained_key"),
      val: t("about.credentials.trained_val"),
    },
    {
      key: t("about.credentials.specialty_key"),
      val: t("about.credentials.specialty_val"),
    },
    {
      key: t("about.credentials.since_key"),
      val: t("about.credentials.since_val"),
    },
  ];

  return (
    <Section>
      <GlobalStyle />
      <Track ref={trackRef}>
        <ImageCol>
          <Img style={{ y: imgY }} />
          <ImgFade />
        </ImageCol>

        <TextCol>
          <EyebrowRow>
            <EyebrowDash
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ transformOrigin: "left" }}
            />
            <Eyebrow
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {t("about.eyebrow")}
            </Eyebrow>
          </EyebrowRow>

          <NameBlock>
            <RevealLine as={Name} delay={0.05}>
              {t("about.name_first")}
            </RevealLine>
            <RevealLine as={NameItalic} delay={0.17}>
              {t("about.name_last")}
            </RevealLine>
          </NameBlock>

          <FadeBlock>
            <TextBlock>
              <BlockQuote>{t("about.quote")}</BlockQuote>
            </TextBlock>
          </FadeBlock>

          <FadeBlock delay={0.05}>
            <Divider style={{ transformOrigin: "left" }} />
          </FadeBlock>

          <FadeBlock>
            <TextBlock>
              <BlockLabel>{t("about.story_label")}</BlockLabel>
              <BlockText>{t("about.story_text")}</BlockText>
            </TextBlock>
          </FadeBlock>

          <FadeBlock delay={0.05}>
            <Divider style={{ transformOrigin: "left" }} />
          </FadeBlock>

          <FadeBlock>
            <TextBlock>
              <BlockLabel>{t("about.approach_label")}</BlockLabel>
              <BlockText>{t("about.approach_text")}</BlockText>
            </TextBlock>
          </FadeBlock>

          <FadeBlock delay={0.05}>
            <Divider style={{ transformOrigin: "left" }} />
          </FadeBlock>

          <FadeBlock>
            <TextBlock style={{ maxWidth: "100%" }}>
              <BlockLabel style={{ marginBottom: "1.8rem" }}>
                {t("about.qualifications_label")}
              </BlockLabel>
              <CredList>
                {credentials.map((c, i) => (
                  <CredRow
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.07,
                      duration: 0.6,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <CredKey>{c.key}</CredKey>
                    <CredVal>{c.val}</CredVal>
                  </CredRow>
                ))}
              </CredList>
            </TextBlock>
          </FadeBlock>

          <FadeBlock>
            <CtaBlock>
              <CtaBtn whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                <Link to={"/contact"} className="link-styles">
                  <span>{t("about.cta_work")}</span>
                </Link>
              </CtaBtn>
              <CtaLink>
                <Link
                  to={"/portfolio"}
                  style={{ color: "brown", textDecoration: "none" }}
                >
                  {t("about.cta_portfolio")}
                  <svg width="18" height="10" viewBox="0 0 18 10" fill="none">
                    <path
                      d="M1 5h16M12 1l5 4-5 4"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </CtaLink>
            </CtaBlock>
          </FadeBlock>

          <FadeBlock delay={0.1}>
            <Signature>— {t("about.name_first")}</Signature>
          </FadeBlock>
        </TextCol>
      </Track>
    </Section>
  );
}
